'use client';

import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import NavBar from '@/components/NavBar';
import HomeFooter from '@/components/HomeFooter';
import EmailAuthModal from '@/components/EmailAuthModal';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';

interface VideoPerformance {
  id: string;
  title: string;
  thumbnail: string;
  thumbnail_alt: string;
  views: number;
  likes_count: number;
  comments_count: number;
  video_status: string;
  created_at: string;
  category: string;
}

interface VideoReport {
  id: string;
  video_id: string;
  category: string;
  details: string | null;
  status: string;
  created_at: string;
}

interface FollowRecord {
  created_at: string;
}

interface StripeAccountStatus {
  connected: boolean;
  stripeAccountId?: string;
  onboardingComplete?: boolean;
  payoutsEnabled?: boolean;
  chargesEnabled?: boolean;
}

interface PayoutRecord {
  id: string;
  amount: number;
  currency: string;
  status: string;
  stripe_transfer_id: string | null;
  stripe_payout_id: string | null;
  payout_method: string;
  description: string | null;
  requested_at: string;
  processed_at: string | null;
}

type DashTab = 'overview' | 'videos' | 'reports' | 'followers' | 'earnings';

const STATUS_COLORS: Record<string, string> = {
  approved: 'bg-green-500 bg-opacity-20 text-green-400 border border-green-500 border-opacity-30',
  pending: 'bg-yellow-500 bg-opacity-20 text-yellow-400 border border-yellow-500 border-opacity-30',
  rejected: 'bg-red-500 bg-opacity-20 text-red-400 border border-red-500 border-opacity-30',
};

const REPORT_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-500 bg-opacity-20 text-yellow-400 border border-yellow-500 border-opacity-30',
  reviewed: 'bg-blue-500 bg-opacity-20 text-blue-400 border border-blue-500 border-opacity-30',
  resolved: 'bg-green-500 bg-opacity-20 text-green-400 border border-green-500 border-opacity-30',
  dismissed: 'bg-gray-500 bg-opacity-20 text-gray-400 border border-gray-500 border-opacity-30',
};

const PAYOUT_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-500 bg-opacity-20 text-yellow-400 border border-yellow-500 border-opacity-30',
  processing: 'bg-blue-500 bg-opacity-20 text-blue-400 border border-blue-500 border-opacity-30',
  paid: 'bg-green-500 bg-opacity-20 text-green-400 border border-green-500 border-opacity-30',
  failed: 'bg-red-500 bg-opacity-20 text-red-400 border border-red-500 border-opacity-30',
  cancelled: 'bg-gray-500 bg-opacity-20 text-gray-400 border border-gray-500 border-opacity-30',
};

function StatCard({
  label,
  value,
  icon,
  highlight,
  sub,
}: {
  label: string;
  value: string | number;
  icon: string;
  highlight?: boolean;
  sub?: string;
}) {
  return (
    <div
      className={`rounded-lg p-4 border flex flex-col gap-1 ${
        highlight
          ? 'bg-brand-red bg-opacity-10 border-brand-red border-opacity-30' :'bg-surface-secondary border-stroke-primary'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <p className="text-text-muted text-xs uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${highlight ? 'text-brand-red' : 'text-text-primary'}`}>
        {value}
      </p>
      {sub && <p className="text-text-muted text-xs">{sub}</p>}
    </div>
  );
}

function CreatorDashboardInner() {
  const supabase = createClient();
  const { user, profile, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [activeTab, setActiveTab] = useState<DashTab>('overview');
  const [showAuthModal, setShowAuthModal] = useState(false);

  const [videos, setVideos] = useState<VideoPerformance[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(false);

  const [reports, setReports] = useState<VideoReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  const [follows, setFollows] = useState<FollowRecord[]>([]);
  const [followerCount, setFollowerCount] = useState(0);
  const [loadingFollowers, setLoadingFollowers] = useState(false);

  // Earnings / Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus>({ connected: false });
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [payouts, setPayouts] = useState<PayoutRecord[]>([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawSuccess, setWithdrawSuccess] = useState('');
  const [stripeNotice, setStripeNotice] = useState('');

  const fetchVideos = useCallback(async () => {
    if (!user) return;
    setLoadingVideos(true);
    const email = user.email || '';
    const name = profile?.full_name || '';
    const { data, error } = await supabase
      .from('videos')
      .select(
        'id, title, thumbnail, thumbnail_alt, views, likes_count, comments_count, video_status, created_at, category'
      )
      .or(`submitted_by.eq.${email},submitted_by.eq.${name}`)
      .order('views', { ascending: false });
    if (!error && data) setVideos(data as VideoPerformance[]);
    setLoadingVideos(false);
  }, [user, profile]);

  const fetchReports = useCallback(async () => {
    if (!user) return;
    setLoadingReports(true);
    const email = user.email || '';
    const name = profile?.full_name || '';

    const { data: videoData } = await supabase
      .from('videos')
      .select('id')
      .or(`submitted_by.eq.${email},submitted_by.eq.${name}`);

    if (videoData && videoData.length > 0) {
      const videoIds = videoData.map((v: { id: string }) => v.id);
      const { data, error } = await supabase
        .from('video_reports')
        .select('id, video_id, category, details, status, created_at')
        .in('video_id', videoIds)
        .order('created_at', { ascending: false });
      if (!error && data) setReports(data as VideoReport[]);
    }
    setLoadingReports(false);
  }, [user, profile]);

  const fetchFollowers = useCallback(async () => {
    if (!user?.id) return;
    setLoadingFollowers(true);
    const { data, error, count } = await supabase
      .from('user_follows')
      .select('created_at', { count: 'exact' })
      .eq('following_id', user.id)
      .order('created_at', { ascending: true });
    if (!error) {
      setFollowerCount(count || 0);
      setFollows((data as FollowRecord[]) || []);
    }
    setLoadingFollowers(false);
  }, [user]);

  const fetchStripeStatus = useCallback(async () => {
    if (!user?.id || !user?.email) return;
    setLoadingStripe(true);
    try {
      const res = await fetch('/api/stripe-connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'check_status', userId: user.id, userEmail: user.email }),
      });
      const data = await res.json();
      if (res.ok) setStripeStatus(data);
    } catch {
      // silently fail
    }
    setLoadingStripe(false);
  }, [user]);

  const fetchPayouts = useCallback(async () => {
    if (!user?.id || !user?.email) return;
    setLoadingPayouts(true);
    try {
      const res = await fetch('/api/stripe-connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_payouts', userId: user.id, userEmail: user.email }),
      });
      const data = await res.json();
      if (res.ok) setPayouts(data.payouts || []);
    } catch {
      // silently fail
    }
    setLoadingPayouts(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchVideos();
      fetchReports();
      fetchFollowers();
    }
  }, [user, fetchVideos, fetchReports, fetchFollowers]);

  useEffect(() => {
    if (user && activeTab === 'earnings') {
      fetchStripeStatus();
      fetchPayouts();
    }
  }, [user, activeTab, fetchStripeStatus, fetchPayouts]);

  // Handle Stripe Connect return
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      setActiveTab('earnings');
      setStripeNotice('✅ Stripe account connected! Refreshing status…');
      fetchStripeStatus();
      fetchPayouts();
      router.replace('/creator/dashboard');
    } else if (stripeParam === 'refresh') {
      setActiveTab('earnings');
      setStripeNotice('⚠️ Onboarding incomplete. Please connect your Stripe account again.');
      router.replace('/creator/dashboard');
    }
  }, [searchParams]);

  const handleConnectStripe = async () => {
    if (!user?.id || !user?.email) return;
    setConnectingStripe(true);
    try {
      const res = await fetch('/api/stripe-connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create_account', userId: user.id, userEmail: user.email }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        setWithdrawError(data.error || 'Failed to start Stripe onboarding');
      }
    } catch {
      setWithdrawError('Failed to connect Stripe account');
    }
    setConnectingStripe(false);
  };

  const handleWithdraw = async () => {
    setWithdrawError('');
    setWithdrawSuccess('');
    const amountCents = Math.round(parseFloat(withdrawAmount) * 100);
    if (!withdrawAmount || isNaN(amountCents) || amountCents < 1000) {
      setWithdrawError('Minimum withdrawal amount is $10.00');
      return;
    }
    setWithdrawing(true);
    try {
      const res = await fetch('/api/stripe-connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'request_payout',
          userId: user?.id,
          userEmail: user?.email,
          amount: amountCents,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setWithdrawSuccess('Withdrawal initiated successfully! Funds will arrive in 1–3 business days.');
        setWithdrawAmount('');
        fetchPayouts();
      } else {
        setWithdrawError(data.error || 'Withdrawal failed. Please try again.');
      }
    } catch {
      setWithdrawError('Withdrawal failed. Please try again.');
    }
    setWithdrawing(false);
  };

  // ── Derived stats ──
  const totalViews = videos.reduce((s, v) => s + (v.views || 0), 0);
  const totalLikes = videos.reduce((s, v) => s + (v.likes_count || 0), 0);
  const totalComments = videos.reduce((s, v) => s + (v.comments_count || 0), 0);
  const engagementRate =
    totalViews > 0 ? (((totalLikes + totalComments) / totalViews) * 100).toFixed(1) : '0.0';
  const approvedCount = videos.filter((v) => v.video_status === 'approved').length;
  const pendingReports = reports.filter((r) => r.status === 'pending').length;

  const viewsChartData = videos.slice(0, 8).map((v) => ({
    name: v.title.length > 18 ? v.title.slice(0, 18) + '…' : v.title,
    views: v.views || 0,
    likes: v.likes_count || 0,
    comments: v.comments_count || 0,
  }));

  const followerGrowthMap: Record<string, number> = {};
  follows.forEach((f) => {
    const month = f.created_at.slice(0, 7);
    followerGrowthMap[month] = (followerGrowthMap[month] || 0) + 1;
  });
  const followerGrowthData = Object.entries(followerGrowthMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([month, count]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      followers: count,
    }));

  let cumulative = 0;
  const cumulativeData = followerGrowthData.map((d) => {
    cumulative += d.followers;
    return { ...d, total: cumulative };
  });

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

  const totalPaid = payouts
    .filter((p) => p.status === 'paid' || p.status === 'processing')
    .reduce((s, p) => s + p.amount, 0);

  const TABS: { id: DashTab; label: string; emoji: string }[] = [
    { id: 'overview', label: 'Overview', emoji: '📊' },
    { id: 'videos', label: 'Video Performance', emoji: '🎬' },
    { id: 'reports', label: 'Reports', emoji: '🚩' },
    { id: 'followers', label: 'Followers', emoji: '👥' },
    { id: 'earnings', label: 'Earnings & Payouts', emoji: '💰' },
  ];

  return (
    <div className="min-h-screen bg-surface-primary flex flex-col">
      <NavBar activePage="other" />

      <main className="flex-1 max-w-5xl mx-auto w-full px-3 sm:px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-5 sm:mb-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1 h-6 bg-brand-red" />
              <h1 className="text-xl sm:text-2xl font-bold text-text-primary uppercase tracking-wide">
                Performance Dashboard
              </h1>
            </div>
            <p className="text-text-secondary text-sm ml-3">
              Views, engagement, reports &amp; follower trends
            </p>
          </div>
          <Link
            href="/creator"
            className="text-sm text-text-secondary border border-stroke-primary rounded-[4px] px-3 py-1.5 hover:text-text-primary hover:bg-surface-tertiary transition-colors self-start sm:self-auto"
          >
            ← Creator Account
          </Link>
        </div>


        {authLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !user ? (
          <div className="max-w-md mx-auto text-center py-12 sm:py-16 px-4">
            <div className="w-16 h-16 rounded-full bg-surface-secondary border border-stroke-primary flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-text-primary mb-2">Creator Sign In Required</h2>
            <p className="text-text-secondary text-sm mb-6">
              Sign in to view your video performance analytics, engagement stats, and follower trends.
            </p>
            <button onClick={() => setShowAuthModal(true)} className="btn-primary px-8 py-3 text-base">
              Sign In / Create Account
            </button>
          </div>
        ) : (
          <div>
            {/* Tabs */}
            <div className="flex gap-0.5 mb-5 border-b border-stroke-primary overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-brand-red text-brand-red' :'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <span>{tab.emoji}</span>
                  {tab.label}
                  {tab.id === 'reports' && pendingReports > 0 && (
                    <span className="bg-brand-red text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                      {pendingReports}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* ── OVERVIEW TAB ── */}
            {activeTab === 'overview' && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                  <StatCard label="Total Views" value={totalViews.toLocaleString()} icon="👁️" />
                  <StatCard label="Total Likes" value={totalLikes.toLocaleString()} icon="👍" />
                  <StatCard label="Comments" value={totalComments.toLocaleString()} icon="💬" />
                  <StatCard
                    label="Engagement Rate"
                    value={`${engagementRate}%`}
                    icon="📈"
                    highlight
                    sub="(likes + comments) / views"
                  />
                  <StatCard label="Videos Uploaded" value={videos.length} icon="🎬" />
                  <StatCard label="Approved" value={approvedCount} icon="✅" />
                  <StatCard label="Followers" value={followerCount.toLocaleString()} icon="👥" />
                  <StatCard
                    label="Open Reports"
                    value={pendingReports}
                    icon="🚩"
                    highlight={pendingReports > 0}
                  />
                </div>

                {viewsChartData.length > 0 && (
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                    <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary">
                      <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">
                        Views by Video (Top 8)
                      </h2>
                    </div>
                    <div className="p-4 sm:p-5">
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={viewsChartData} margin={{ top: 4, right: 8, left: -10, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis
                            dataKey="name"
                            tick={{ fill: '#9ca3af', fontSize: 10 }}
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                          />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
                          <Tooltip
                            contentStyle={{
                              background: '#1e1e1e',
                              border: '1px solid #333',
                              borderRadius: 6,
                              color: '#f3f4f6',
                              fontSize: 12,
                            }}
                          />
                          <Bar dataKey="views" fill="#e53e3e" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {cumulativeData.length > 0 && (
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                    <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary">
                      <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">
                        Follower Growth (Last 12 Months)
                      </h2>
                    </div>
                    <div className="p-4 sm:p-5">
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={cumulativeData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
                          <Tooltip
                            contentStyle={{
                              background: '#1e1e1e',
                              border: '1px solid #333',
                              borderRadius: 6,
                              color: '#f3f4f6',
                              fontSize: 12,
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#e53e3e"
                            strokeWidth={2}
                            dot={{ fill: '#e53e3e', r: 3 }}
                            name="Total Followers"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {videos.length === 0 && (
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg text-center py-16 px-4">
                    <div className="text-4xl mb-3">📊</div>
                    <p className="text-text-secondary font-medium mb-1">No data yet</p>
                    <p className="text-text-muted text-sm">
                      Submit videos to start tracking your performance analytics.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── VIDEO PERFORMANCE TAB ── */}
            {activeTab === 'videos' && (
              <div className="space-y-4">
                {viewsChartData.length > 0 && (
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                    <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary">
                      <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">
                        Engagement Breakdown (Top 8 Videos)
                      </h2>
                      <p className="text-text-muted text-xs mt-0.5">Views, likes, and comments per video</p>
                    </div>
                    <div className="p-4 sm:p-5">
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={viewsChartData} margin={{ top: 4, right: 8, left: -10, bottom: 44 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis
                            dataKey="name"
                            tick={{ fill: '#9ca3af', fontSize: 10 }}
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                          />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
                          <Tooltip
                            contentStyle={{
                              background: '#1e1e1e',
                              border: '1px solid #333',
                              borderRadius: 6,
                              color: '#f3f4f6',
                              fontSize: 12,
                            }}
                          />
                          <Bar dataKey="views" fill="#e53e3e" radius={[3, 3, 0, 0]} name="Views" />
                          <Bar dataKey="likes" fill="#f59e0b" radius={[3, 3, 0, 0]} name="Likes" />
                          <Bar dataKey="comments" fill="#3b82f6" radius={[3, 3, 0, 0]} name="Comments" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                  <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary flex items-center justify-between">
                    <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">
                      All Videos — Performance
                    </h2>
                    <span className="text-text-muted text-xs">{videos.length} video{videos.length !== 1 ? 's' : ''}</span>
                  </div>

                  {loadingVideos ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-text-muted">
                      <div className="w-5 h-5 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Loading…</span>
                    </div>
                  ) : videos.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <p className="text-text-secondary font-medium mb-1">No videos found</p>
                      <p className="text-text-muted text-sm">Submit videos using this account to see performance data.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-stroke-primary">
                      {videos.map((video) => {
                        const eng =
                          (video.views || 0) > 0
                            ? ((((video.likes_count || 0) + (video.comments_count || 0)) / video.views) * 100).toFixed(1)
                            : '0.0';
                        return (
                          <div
                            key={video.id}
                            className="flex items-start gap-3 p-3 sm:p-4 hover:bg-surface-tertiary transition-colors"
                          >
                            <div className="w-20 sm:w-24 h-12 sm:h-14 rounded overflow-hidden flex-shrink-0 bg-surface-tertiary">
                              {video.thumbnail ? (
                                <img
                                  src={video.thumbnail}
                                  alt={video.thumbnail_alt || video.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 flex-wrap">
                                <Link
                                  href={`/video/${video.id}`}
                                  className="text-text-primary font-medium text-sm hover:text-brand-red transition-colors line-clamp-2 flex-1 min-w-0"
                                >
                                  {video.title}
                                </Link>
                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[video.video_status] || STATUS_COLORS.pending}`}
                                >
                                  {video.video_status}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-2">
                                <div>
                                  <p className="text-text-muted text-xs uppercase tracking-wide">Views</p>
                                  <p className="text-text-primary font-bold text-sm">{(video.views || 0).toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-text-muted text-xs uppercase tracking-wide">Likes</p>
                                  <p className="text-text-primary font-bold text-sm">{(video.likes_count || 0).toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-text-muted text-xs uppercase tracking-wide">Comments</p>
                                  <p className="text-text-primary font-bold text-sm">{(video.comments_count || 0).toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-text-muted text-xs uppercase tracking-wide">Engagement</p>
                                  <p className="text-brand-red font-bold text-sm">{eng}%</p>
                                </div>
                              </div>
                              <p className="text-text-muted text-xs mt-1.5">{formatDate(video.created_at)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── REPORTS TAB ── */}
            {activeTab === 'reports' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                  {[
                    { label: 'Total Reports', value: reports.length, icon: '🚩' },
                    { label: 'Pending Review', value: reports.filter((r) => r.status === 'pending').length, icon: '⏳', highlight: pendingReports > 0 },
                    { label: 'Resolved', value: reports.filter((r) => r.status === 'resolved').length, icon: '✅' },
                    { label: 'Dismissed', value: reports.filter((r) => r.status === 'dismissed').length, icon: '🚫' },
                  ].map((s) => (
                    <StatCard key={s.label} label={s.label} value={s.value} icon={s.icon} highlight={s.highlight} />
                  ))}
                </div>

                <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                  <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary">
                    <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">Reports on Your Videos</h2>
                    <p className="text-text-muted text-xs mt-0.5">Reports submitted by viewers — reviewed by our moderation team</p>
                  </div>

                  {loadingReports ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-text-muted">
                      <div className="w-5 h-5 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Loading reports…</span>
                    </div>
                  ) : reports.length === 0 ? (
                    <div className="text-center py-14 px-4">
                      <div className="text-4xl mb-3">✅</div>
                      <p className="text-text-secondary font-medium mb-1">No reports</p>
                      <p className="text-text-muted text-sm">None of your videos have been reported.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-stroke-primary">
                      {reports.map((report) => {
                        const video = videos.find((v) => v.id === report.video_id);
                        return (
                          <div key={report.id} className="flex items-start gap-3 p-3 sm:p-4 hover:bg-surface-tertiary transition-colors">
                            <div className="w-8 h-8 rounded-full bg-surface-tertiary flex items-center justify-center flex-shrink-0 mt-0.5 text-base">
                              🚩
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-text-primary font-medium text-sm capitalize">
                                  {report.category.replace(/_/g, ' ')}
                                </span>
                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${REPORT_STATUS_COLORS[report.status] || REPORT_STATUS_COLORS.pending}`}
                                >
                                  {report.status}
                                </span>
                              </div>
                              {video && (
                                <Link
                                  href={`/video/${video.id}`}
                                  className="text-text-muted text-xs hover:text-brand-red transition-colors line-clamp-1 block"
                                >
                                  Video: {video.title}
                                </Link>
                              )}
                              {report.details && (
                                <p className="text-text-secondary text-xs mt-1 italic line-clamp-2">
                                  &quot;{report.details}&quot;
                                </p>
                              )}
                              <p className="text-text-muted text-xs mt-1">{formatDate(report.created_at)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── FOLLOWERS TAB ── */}
            {activeTab === 'followers' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  <StatCard label="Total Followers" value={followerCount.toLocaleString()} icon="👥" highlight />
                  <StatCard
                    label="New This Month"
                    value={
                      follows.filter((f) => {
                        const thisMonth = new Date().toISOString().slice(0, 7);
                        return f.created_at.startsWith(thisMonth);
                      }).length
                    }
                    icon="📈"
                  />
                  <StatCard
                    label="New This Week"
                    value={
                      follows.filter((f) => {
                        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
                        return f.created_at >= weekAgo;
                      }).length
                    }
                    icon="🔥"
                  />
                </div>

                {cumulativeData.length > 0 ? (
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                    <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary">
                      <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">
                        Follower Growth Over Time
                      </h2>
                      <p className="text-text-muted text-xs mt-0.5">Cumulative followers by month</p>
                    </div>
                    <div className="p-4 sm:p-5">
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={cumulativeData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
                          <Tooltip
                            contentStyle={{
                              background: '#1e1e1e',
                              border: '1px solid #333',
                              borderRadius: 6,
                              color: '#f3f4f6',
                              fontSize: 12,
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#e53e3e"
                            strokeWidth={2.5}
                            dot={{ fill: '#e53e3e', r: 4 }}
                            name="Total Followers"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg text-center py-16 px-4">
                    <div className="text-4xl mb-3">👥</div>
                    <p className="text-text-secondary font-medium mb-1">
                      {loadingFollowers ? 'Loading follower data…' : 'No followers yet'}
                    </p>
                    <p className="text-text-muted text-sm">
                      As viewers follow your profile, growth trends will appear here.
                    </p>
                  </div>
                )}

                {followerGrowthData.length > 0 && (
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                    <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary">
                      <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">
                        Monthly New Followers
                      </h2>
                    </div>
                    <div className="p-4 sm:p-5">
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={followerGrowthData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} allowDecimals={false} />
                          <Tooltip
                            contentStyle={{
                              background: '#1e1e1e',
                              border: '1px solid #333',
                              borderRadius: 6,
                              color: '#f3f4f6',
                              fontSize: 12,
                            }}
                          />
                          <Bar dataKey="followers" fill="#e53e3e" radius={[3, 3, 0, 0]} name="New Followers" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── EARNINGS & PAYOUTS TAB ── */}
            {activeTab === 'earnings' && (
              <div className="space-y-5">
                {/* Stripe return notice */}
                {stripeNotice && (
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg px-4 py-3 text-sm text-text-secondary">
                    {stripeNotice}
                  </div>
                )}

                {loadingStripe ? (
                  <div className="flex items-center justify-center py-16 gap-2 text-text-muted">
                    <div className="w-6 h-6 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Checking Stripe status…</span>
                  </div>
                ) : !stripeStatus.connected ? (
                  /* ── NOT CONNECTED ── */
                  <div className="bg-surface-secondary border border-stroke-primary rounded-lg p-6 sm:p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-surface-tertiary border border-stroke-primary flex items-center justify-center mx-auto mb-4 text-3xl">
                      💳
                    </div>
                    <h2 className="text-text-primary font-bold text-lg mb-2">Connect Stripe to Withdraw Earnings</h2>
                    <p className="text-text-secondary text-sm mb-6 max-w-md mx-auto">
                      Connect your Stripe account to receive payouts from video views and ad revenue directly to your bank account.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-6">
                      <div className="flex items-center gap-2 text-text-muted text-xs">
                        <span className="text-green-400">✓</span> Secure bank transfers
                      </div>
                      <div className="flex items-center gap-2 text-text-muted text-xs">
                        <span className="text-green-400">✓</span> 1–3 business day payouts
                      </div>
                      <div className="flex items-center gap-2 text-text-muted text-xs">
                        <span className="text-green-400">✓</span> Powered by Stripe Connect
                      </div>
                    </div>
                    <button
                      onClick={handleConnectStripe}
                      disabled={connectingStripe}
                      className="btn-primary px-8 py-3 text-base disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {connectingStripe ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Connecting…
                        </>
                      ) : (
                        'Connect Stripe Account'
                      )}
                    </button>
                  </div>
                ) : !stripeStatus.onboardingComplete || !stripeStatus.payoutsEnabled ? (
                  /* ── ONBOARDING INCOMPLETE ── */
                  <div className="bg-surface-secondary border border-yellow-500 border-opacity-30 rounded-lg p-6 text-center">
                    <div className="text-3xl mb-3">⚠️</div>
                    <h2 className="text-text-primary font-bold text-base mb-2">Complete Stripe Onboarding</h2>
                    <p className="text-text-secondary text-sm mb-5">
                      Your Stripe account is connected but payouts are not yet enabled. Complete the onboarding to start withdrawing.
                    </p>
                    <button
                      onClick={handleConnectStripe}
                      disabled={connectingStripe}
                      className="btn-primary px-6 py-2.5 text-sm disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {connectingStripe ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Loading…
                        </>
                      ) : (
                        'Complete Onboarding'
                      )}
                    </button>
                  </div>
                ) : (
                  /* ── CONNECTED & ENABLED ── */
                  <>
                    {/* Status banner */}
                    <div className="bg-green-500 bg-opacity-10 border border-green-500 border-opacity-30 rounded-lg px-4 py-3 flex items-center gap-3">
                      <span className="text-green-400 text-lg">✅</span>
                      <div>
                        <p className="text-green-400 font-medium text-sm">Stripe Account Connected</p>
                        <p className="text-text-muted text-xs">Payouts enabled · Account: {stripeStatus.stripeAccountId}</p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                      <StatCard
                        label="Total Withdrawn"
                        value={formatCurrency(totalPaid)}
                        icon="💸"
                        highlight
                      />
                      <StatCard
                        label="Payout Requests"
                        value={payouts.length}
                        icon="📋"
                      />
                      <StatCard
                        label="Processing"
                        value={payouts.filter((p) => p.status === 'processing').length}
                        icon="⏳"
                      />
                    </div>

                    {/* Withdraw form */}
                    <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                      <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary">
                        <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">Request Withdrawal</h2>
                        <p className="text-text-muted text-xs mt-0.5">Minimum $10.00 · Funds arrive in 1–3 business days</p>
                      </div>
                      <div className="p-4 sm:p-5">
                        {withdrawSuccess && (
                          <div className="mb-4 bg-green-500 bg-opacity-10 border border-green-500 border-opacity-30 rounded-lg px-4 py-3 text-green-400 text-sm">
                            {withdrawSuccess}
                          </div>
                        )}
                        {withdrawError && (
                          <div className="mb-4 bg-red-500 bg-opacity-10 border border-red-500 border-opacity-30 rounded-lg px-4 py-3 text-red-400 text-sm">
                            {withdrawError}
                          </div>
                        )}
                        <div className="flex gap-3 items-end flex-wrap">
                          <div className="flex-1 min-w-[160px]">
                            <label className="block text-text-muted text-xs uppercase tracking-wide mb-1.5">
                              Amount (USD)
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">$</span>
                              <input
                                type="number"
                                min="10"
                                step="0.01"
                                value={withdrawAmount}
                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                placeholder="0.00"
                                className="w-full bg-surface-tertiary border border-stroke-primary rounded-[4px] pl-7 pr-3 py-2.5 text-text-primary text-sm focus:outline-none focus:border-brand-red transition-colors"
                              />
                            </div>
                          </div>
                          <button
                            onClick={handleWithdraw}
                            disabled={withdrawing || !withdrawAmount}
                            className="btn-primary px-6 py-2.5 text-sm disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2 whitespace-nowrap"
                          >
                            {withdrawing ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Processing…
                              </>
                            ) : (
                              'Withdraw Earnings'
                            )}
                          </button>
                        </div>
                        <p className="text-text-muted text-xs mt-3">
                          ⚠️ Ensure your available balance covers the withdrawal amount. Stripe may hold funds for new accounts.
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* Payout history */}
                <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                  <div className="px-4 sm:px-5 py-3 border-b border-stroke-primary flex items-center justify-between">
                    <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">Payout History</h2>
                    <span className="text-text-muted text-xs">{payouts.length} record{payouts.length !== 1 ? 's' : ''}</span>
                  </div>

                  {loadingPayouts ? (
                    <div className="flex items-center justify-center py-10 gap-2 text-text-muted">
                      <div className="w-5 h-5 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Loading payouts…</span>
                    </div>
                  ) : payouts.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <div className="text-3xl mb-3">💸</div>
                      <p className="text-text-secondary font-medium mb-1">No payouts yet</p>
                      <p className="text-text-muted text-sm">Your withdrawal history will appear here.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-stroke-primary">
                      {payouts.map((payout) => (
                        <div key={payout.id} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-surface-tertiary transition-colors">
                          <div className="w-9 h-9 rounded-full bg-surface-tertiary flex items-center justify-center flex-shrink-0 text-base">
                            💸
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <p className="text-text-primary font-bold text-sm">{formatCurrency(payout.amount)}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PAYOUT_STATUS_COLORS[payout.status] || PAYOUT_STATUS_COLORS.pending}`}>
                                {payout.status}
                              </span>
                            </div>
                            <p className="text-text-muted text-xs mt-0.5">{formatDate(payout.requested_at)}</p>
                            {payout.stripe_transfer_id && (
                              <p className="text-text-muted text-xs mt-0.5 font-mono truncate">
                                Transfer: {payout.stripe_transfer_id}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <HomeFooter />

      <EmailAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />
    </div>
  );
}

export default function CreatorDashboardPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-surface-primary flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <CreatorDashboardInner />
    </Suspense>
  );
}